/**
 * User Management Page
 * Admin interface for managing user roles and permissions
 */

import React, { useState, useEffect } from 'react';
import { UserRole, RbacRoleWithPermissions } from '../lib/types';
import { usePermissions } from '../contexts/PermissionContext';
import { SectionGuard, ViewGuard } from '../components/auth/PermissionGuards';
import {
  apiRequest,
  createUserApiKey,
  listUserApiKeys,
  revokeUserApiKey,
  UserApiKeyMetadata
} from '../lib/api';
import { SecurityLayout } from './security/layout';
import { MultiRoleSelect } from '../components/ui/multi-role-select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { CreateInvitationModal } from '../components/invitations/CreateInvitationModal';
import { InvitePLUserModal } from '../components/invitations/InvitePLUserModal';
import { InvitationsList } from '../components/invitations/InvitationsList';

interface UserManagementData {
  users: Array<{
    userId: string;
    email: string;
    displayName?: string;
    role: string;
    roles?: string[];
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    lastLoginAt?: string;
  }>;
}

interface CreateUserFormData {
  userId: string;
  email: string;
  displayName: string;
  roles: string[];
}

interface EntraUserSearchResult {
  id: string;
  displayName: string;
  userPrincipalName: string;
}

export default function UserManagementPage() {
  const { refresh } = usePermissions();
  const [users, setUsers] = useState<UserManagementData['users']>([]);
  const [roles, setRoles] = useState<RbacRoleWithPermissions[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createFormData, setCreateFormData] = useState<CreateUserFormData>({
    userId: '',
    email: '',
    displayName: '',
    roles: []
  });
  const [entraQuery, setEntraQuery] = useState('');
  const [entraResults, setEntraResults] = useState<EntraUserSearchResult[]>([]);
  const [entraLoading, setEntraLoading] = useState(false);
  const [entraError, setEntraError] = useState<string | null>(null);
  const [entraSuppressSearch, setEntraSuppressSearch] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showBootstrap, setShowBootstrap] = useState(false);
  const [editRolesOpen, setEditRolesOpen] = useState(false);
  const [editRolesUser, setEditRolesUser] = useState<UserManagementData['users'][number] | null>(null);
  const [editRoles, setEditRoles] = useState<string[]>([]);
  const [isUpdatingRoles, setIsUpdatingRoles] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showInvitePLModal, setShowInvitePLModal] = useState(false);
  const [apiKeysOpen, setApiKeysOpen] = useState(false);
  const [apiKeysUser, setApiKeysUser] = useState<UserManagementData['users'][number] | null>(null);
  const [apiKeys, setApiKeys] = useState<UserApiKeyMetadata[]>([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [newApiKeyName, setNewApiKeyName] = useState('');
  const [newApiKeyScopes, setNewApiKeyScopes] = useState<string[]>(['contextGraph.view']);
  const [newApiKeyExpiresAt, setNewApiKeyExpiresAt] = useState('');
  const [newApiKeySecret, setNewApiKeySecret] = useState<string | null>(null);

  const contextGraphScopeOptions = [
    'contextGraph.view',
    'contextGraph.edit',
    'contextGraph.execute',
    'contextGraph.export'
  ];

  /**
   * Load users from API
   */
  const loadUsers = async () => {
    try {
      setIsLoading(true);
      const response = await apiRequest('GET', '/api/users');
      const data = await response.json();

      if (response.ok) {
        setUsers(data.users || []);
        setMessage(null);
      } else if (response.status === 403) {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.message });

        // Check if this is a bootstrap scenario
        if (errorData.message.toLowerCase().includes('administrator')) {
          setShowBootstrap(true);
        }
      } else {
        throw new Error(`Failed to load users: ${response.status}`);
      }
    } catch (err: any) {
      console.error('Error loading users:', err);
      setMessage({ type: 'error', text: 'Failed to load users' });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Load roles from API
   */
  const loadRoles = async () => {
    try {
      const response = await apiRequest('GET', '/api/roles');
      if (response.ok) {
        const data = await response.json();
        setRoles(data.roles || []);
        if (createFormData.roles.length === 0 && data.roles?.length) {
          setCreateFormData(prev => ({ ...prev, roles: [data.roles[0].name] }));
        }
      }
    } catch (err) {
      console.error('Error loading roles:', err);
    }
  };

  /**
   * Entra user search (enabled users only; server-enforced)
   */
  useEffect(() => {
    if (!showCreateForm) return;
    if (entraSuppressSearch) {
      setEntraSuppressSearch(false);
      return;
    }

    const q = entraQuery.trim();
    if (q.length < 2) {
      setEntraResults([]);
      setEntraError(null);
      return;
    }

    const handle = setTimeout(async () => {
      try {
        setEntraLoading(true);
        setEntraError(null);
        const params = new URLSearchParams({ q, limit: '10' });
        const response = await apiRequest('GET', `/api/entra/users/search?${params.toString()}`);
        if (!response.ok) {
          const err = await response.json().catch(() => null);
          throw new Error(err?.message || `Search failed (${response.status})`);
        }
        const data = await response.json();
        setEntraResults(data.users || []);
      } catch (err: any) {
        setEntraResults([]);
        setEntraError(err?.message || 'Failed to search Entra users');
      } finally {
        setEntraLoading(false);
      }
    }, 250);

    return () => clearTimeout(handle);
  }, [entraQuery, showCreateForm]);

  const handleSelectEntraUser = (u: EntraUserSearchResult) => {
    setCreateFormData(prev => ({
      ...prev,
      userId: u.id,
      email: u.userPrincipalName,
      displayName: u.displayName || prev.displayName,
    }));
    setEntraSuppressSearch(true);
    setEntraQuery(`${u.displayName} (${u.userPrincipalName})`);
    setEntraResults([]);
  };

  const handleCreateRolesChange = (selected: string[]) => {
    setCreateFormData(prev => ({ ...prev, roles: selected }));
  };

  /**
   * Handle bootstrap process
   */
  const handleBootstrap = async () => {
    try {
      setIsSubmitting(true);
      const response = await apiRequest('POST', '/api/users/bootstrap', {
        email: 'admin@example.com', // This will be overridden by the actual user's email
        displayName: 'Administrator'
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Bootstrap completed! You are now an administrator.' });
        setShowBootstrap(false);
        await refresh(); // Refresh permissions
        await loadUsers(); // Reload users
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.message });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: 'Bootstrap failed' });
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle create user
   */
  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);
      const response = await apiRequest('POST', '/api/users', createFormData);

      if (response.ok) {
        setMessage({ type: 'success', text: 'User created successfully' });
        setShowCreateForm(false);
        setCreateFormData({
          userId: '',
          email: '',
          displayName: '',
          roles: []
        });
        setEntraQuery('');
        setEntraResults([]);
        setEntraError(null);
        await loadUsers();
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.message });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: 'Failed to create user' });
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle user role update
   */
  const handleUpdateUserRoles = async (userId: string, newRoles: string[]) => {
    try {
      if (!newRoles || newRoles.length === 0) {
        setMessage({ type: 'error', text: 'At least one role must be selected' });
        return;
      }

      const response = await apiRequest('PUT', `/api/users/${userId}`, { roles: newRoles });

      if (response.ok) {
        setMessage({ type: 'success', text: 'User roles updated successfully' });
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.message });
        await loadUsers(); // revert any optimistic updates
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: 'Failed to update user role' });
      await loadUsers(); // revert any optimistic updates
    }
  };

  const openEditRoles = (user: UserManagementData['users'][number]) => {
    const currentRoles = (user.roles && user.roles.length > 0) ? user.roles : [user.role];
    setEditRolesUser(user);
    setEditRoles(currentRoles);
    setEditRolesOpen(true);
  };

  const saveEditRoles = async () => {
    if (!editRolesUser) return;
    if (!editRoles || editRoles.length === 0) {
      setMessage({ type: 'error', text: 'At least one role must be selected' });
      return;
    }

    setIsUpdatingRoles(true);
    try {
      const userId = editRolesUser.userId;
      // Optimistic update
      setUsers(prev => prev.map(u => {
        if (u.userId !== userId) return u;
        return { ...u, roles: editRoles, role: editRoles[0] };
      }));

      await handleUpdateUserRoles(userId, editRoles);
      setEditRolesOpen(false);
      setEditRolesUser(null);
    } finally {
      setIsUpdatingRoles(false);
    }
  };

  const openApiKeys = async (user: UserManagementData['users'][number]) => {
    setApiKeysUser(user);
    setApiKeysOpen(true);
    setNewApiKeyName('');
    setNewApiKeyScopes(['contextGraph.view']);
    setNewApiKeyExpiresAt('');
    setNewApiKeySecret(null);
    setApiKeysLoading(true);
    try {
      setApiKeys(await listUserApiKeys(user.userId));
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.message || 'Failed to load API keys' });
    } finally {
      setApiKeysLoading(false);
    }
  };

  const toggleApiKeyScope = (scope: string) => {
    setNewApiKeyScopes(prev => {
      if (prev.includes(scope)) return prev.filter(s => s !== scope);
      return [...prev, scope];
    });
  };

  const handleCreateApiKey = async () => {
    if (!apiKeysUser || !newApiKeyName.trim() || newApiKeyScopes.length === 0) return;
    setApiKeysLoading(true);
    try {
      const created = await createUserApiKey(apiKeysUser.userId, {
        name: newApiKeyName.trim(),
        scopes: newApiKeyScopes,
        expiresAt: newApiKeyExpiresAt || null
      });
      setNewApiKeySecret(created.secret);
      setApiKeys(await listUserApiKeys(apiKeysUser.userId));
      setNewApiKeyName('');
      setNewApiKeyScopes(['contextGraph.view']);
      setNewApiKeyExpiresAt('');
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.message || 'Failed to create API key' });
    } finally {
      setApiKeysLoading(false);
    }
  };

  const handleRevokeApiKey = async (apiKeyId: string) => {
    if (!apiKeysUser || !confirm('Revoke this API key?')) return;
    setApiKeysLoading(true);
    try {
      await revokeUserApiKey(apiKeysUser.userId, apiKeyId);
      setApiKeys(await listUserApiKeys(apiKeysUser.userId));
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.message || 'Failed to revoke API key' });
    } finally {
      setApiKeysLoading(false);
    }
  };

  /**
   * Handle deactivate user
   */
  const handleDeactivateUser = async (userId: string) => {
    if (!confirm('Are you sure you want to deactivate this user?')) {
      return;
    }

    try {
      const response = await apiRequest('DELETE', `/api/users/${userId}`);

      if (response.ok) {
        setMessage({ type: 'success', text: 'User deactivated successfully' });
        await loadUsers();
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.message });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: 'Failed to deactivate user' });
    }
  };

  /**
   * Load users on component mount
   */
  useEffect(() => {
    loadUsers();
    loadRoles();
  }, []);

  useEffect(() => {
    if (!showCreateForm) return;
    if (createFormData.roles.length === 0 && roles.length > 0) {
      setCreateFormData(prev => ({ ...prev, roles: [roles[0].name] }));
    }
  }, [showCreateForm, roles, createFormData.roles.length]);

  // Permission errors are now handled globally by PermissionProvider

  return (
    <ViewGuard
      section="userManagement"
      fallback={
        <div className="flex items-center justify-center h-full">
          <div className="text-center p-8">
            <div className="mb-4">
              <span className="material-icons text-6xl text-muted-foreground">block</span>
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-2">Access Denied</h1>
            <p className="text-muted-foreground">You don't have permission to view User Management</p>
          </div>
        </div>
      }
    >
      <SecurityLayout>
        <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">User Management</h1>
          <p className="text-muted-foreground mt-2">Manage user access and permissions</p>
        </div>

        {/* Messages */}
        {message && (
          <div className={`mb-6 p-4 rounded-md ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            {message.text}
          </div>
        )}

        {/* Bootstrap Section */}
        {showBootstrap && (
          <div className="mb-8 p-6 bg-blue-50 border border-blue-200 rounded-lg">
            <h2 className="text-lg font-semibold text-blue-900 mb-2">First Time Setup</h2>
            <p className="text-blue-800 mb-4">
              It looks like this is the first time setting up user management.
              Click the button below to set yourself up as an administrator.
            </p>
            <button
              onClick={handleBootstrap}
              disabled={isSubmitting}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {isSubmitting ? 'Setting up...' : 'Become Administrator'}
            </button>
          </div>
        )}

        {/* Action Bar */}
        <SectionGuard section="userManagement" operation="edit">
          <div className="mb-6 flex gap-3">
            <button
              onClick={() => setShowInvitePLModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Invite Peter Lang User
            </button>
            <button
              onClick={() => setShowInviteModal(true)}
              className="inline-flex items-center px-4 py-2 border border-border text-sm font-medium rounded-md shadow-sm text-foreground bg-card hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Invite External User
            </button>
            <button
              onClick={() => setShowCreateForm(true)}
              className="inline-flex items-center px-4 py-2 border border-border text-sm font-medium rounded-md shadow-sm text-foreground bg-card hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Create User Manually
            </button>
          </div>
        </SectionGuard>

        {/* Create User Form */}
        {showCreateForm && (
          <div className="mb-8 bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4">Add New User</h2>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Search Entra (UPN or Display Name)</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={entraQuery}
                      onChange={(e) => setEntraQuery(e.target.value)}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Start typing…"
                    />
                    {(entraLoading || entraResults.length > 0) && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-auto">
                        {entraLoading && (
                          <div className="p-3 text-sm text-gray-500">Searching…</div>
                        )}
                        {!entraLoading && entraResults.map(u => (
                          <button
                            key={u.id}
                            type="button"
                            onClick={() => handleSelectEntraUser(u)}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50"
                          >
                            <div className="text-sm font-medium text-gray-900">{u.displayName}</div>
                            <div className="text-xs text-gray-500">{u.userPrincipalName}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {entraError && (
                    <div className="mt-1 text-sm text-red-600">{entraError}</div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">User ID</label>
                  <input
                    type="text"
                    required
                    value={createFormData.userId}
                    onChange={(e) => setCreateFormData(prev => ({ ...prev, userId: e.target.value }))}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Azure AD User ID"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    required
                    value={createFormData.email}
                    onChange={(e) => setCreateFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="user@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Display Name</label>
                  <input
                    type="text"
                    value={createFormData.displayName}
                    onChange={(e) => setCreateFormData(prev => ({ ...prev, displayName: e.target.value }))}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Full Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Roles</label>
                  <div className="mt-1">
                    <MultiRoleSelect
                      options={roles.map(r => ({ value: r.name, label: r.name.replace('_', ' ') }))}
                      value={createFormData.roles}
                      onChange={handleCreateRolesChange}
                      placeholder="Select roles…"
                      hideSelectedOptions={true}
                    />
                  </div>
                  {createFormData.roles.length === 0 && (
                    <div className="mt-1 text-sm text-red-600">Select at least one role</div>
                  )}
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {isSubmitting ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white shadow overflow-hidden rounded-md">
          <div className="px-4 py-5 sm:p-6">
            <h2 className="text-lg font-semibold mb-4">Users</h2>

            {isLoading ? (
              <div className="text-center py-4">Loading users...</div>
            ) : users.length === 0 ? (
              <div className="text-center py-4 text-gray-500">No users found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Roles</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((user) => (
                      <tr key={user.userId}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {user.displayName || user.email}
                            </div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <SectionGuard section="userManagement" operation="edit" fallback={
                            <span className="text-sm text-gray-900">{(user.roles && user.roles.length > 0) ? user.roles.join(', ') : user.role}</span>
                          }>
                            <div className="flex items-start gap-2 min-w-[320px]">
                              <div className="flex flex-wrap gap-1">
                                {((user.roles && user.roles.length > 0) ? user.roles : [user.role]).map(r => (
                                  <span
                                    key={r}
                                    className="inline-flex items-center px-2 py-1 text-xs rounded-md bg-gray-100 text-gray-800"
                                  >
                                    {r.replace('_', ' ')}
                                  </span>
                                ))}
                              </div>
                              <button
                                type="button"
                                onClick={() => openEditRoles(user)}
                                className="ml-auto text-indigo-600 hover:text-indigo-900 text-sm"
                              >
                                Edit
                              </button>
                            </div>
                          </SectionGuard>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.lastLoginAt
                            ? new Date(user.lastLoginAt).toLocaleDateString()
                            : 'Never'
                          }
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <SectionGuard section="userManagement" operation="edit">
                            <button
                              onClick={() => openApiKeys(user)}
                              className="text-indigo-600 hover:text-indigo-900 mr-4"
                            >
                              API Keys
                            </button>
                            {user.isActive && (
                              <button
                                onClick={() => handleDeactivateUser(user.userId)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Deactivate
                              </button>
                            )}
                          </SectionGuard>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Invitations Section */}
        <div className="mt-8">
          <InvitationsList onInvitationChange={loadUsers} />
        </div>

        <Dialog
          open={editRolesOpen}
          onOpenChange={(open) => {
            setEditRolesOpen(open);
            if (!open) {
              setEditRolesUser(null);
            }
          }}
        >
          <DialogContent className="sm:max-w-[520px]">
            <DialogHeader>
              <DialogTitle>Edit roles</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="text-sm text-gray-600">
                {editRolesUser ? (editRolesUser.displayName || editRolesUser.email) : ''}
              </div>
              <MultiRoleSelect
                options={roles.map(r => ({ value: r.name, label: r.name.replace('_', ' ') }))}
                value={editRoles}
                onChange={setEditRoles}
                placeholder="Select roles…"
                hideSelectedOptions={true}
              />
              {editRoles.length === 0 && (
                <div className="text-sm text-red-600">Select at least one role</div>
              )}
            </div>
            <DialogFooter>
              <button
                type="button"
                onClick={() => setEditRolesOpen(false)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                disabled={isUpdatingRoles}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveEditRoles}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                disabled={isUpdatingRoles || editRoles.length === 0}
              >
                {isUpdatingRoles ? 'Saving…' : 'Save'}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={apiKeysOpen}
          onOpenChange={(open) => {
            setApiKeysOpen(open);
            if (!open) {
              setApiKeysUser(null);
              setApiKeys([]);
              setNewApiKeySecret(null);
            }
          }}
        >
          <DialogContent className="sm:max-w-[760px]">
            <DialogHeader>
              <DialogTitle>API keys</DialogTitle>
            </DialogHeader>
            <div className="space-y-5">
              <div className="text-sm text-gray-600">
                {apiKeysUser ? (apiKeysUser.displayName || apiKeysUser.email) : ''}
              </div>

              {newApiKeySecret && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                  <div className="text-sm font-medium text-amber-900">Copy this key now. It will not be shown again.</div>
                  <div className="mt-2 flex items-center gap-2">
                    <code className="flex-1 break-all rounded bg-white px-2 py-1 text-xs text-amber-950">{newApiKeySecret}</code>
                    <button
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(newApiKeySecret)}
                      className="rounded-md border border-amber-300 bg-white px-3 py-1 text-sm text-amber-900 hover:bg-amber-100"
                    >
                      Copy
                    </button>
                  </div>
                </div>
              )}

              <div className="rounded-md border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-900">Generate key</h3>
                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <input
                      value={newApiKeyName}
                      onChange={(event) => setNewApiKeyName(event.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                      placeholder="Codex context graph"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Expires</label>
                    <input
                      type="date"
                      value={newApiKeyExpiresAt}
                      onChange={(event) => setNewApiKeyExpiresAt(event.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                    />
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-3">
                  {contextGraphScopeOptions.map(scope => (
                    <label key={scope} className="inline-flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={newApiKeyScopes.includes(scope)}
                        onChange={() => toggleApiKeyScope(scope)}
                      />
                      {scope}
                    </label>
                  ))}
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={handleCreateApiKey}
                    disabled={apiKeysLoading || !newApiKeyName.trim() || newApiKeyScopes.length === 0}
                    className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                  >
                    Generate key
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto rounded-md border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Name</th>
                      <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Prefix</th>
                      <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Scopes</th>
                      <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">Last used</th>
                      <th className="px-4 py-2 text-right text-xs font-medium uppercase text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {apiKeysLoading ? (
                      <tr><td colSpan={5} className="px-4 py-4 text-center text-sm text-gray-500">Loading API keys...</td></tr>
                    ) : apiKeys.length === 0 ? (
                      <tr><td colSpan={5} className="px-4 py-4 text-center text-sm text-gray-500">No API keys</td></tr>
                    ) : apiKeys.map(key => (
                      <tr key={key.apiKeyId}>
                        <td className="px-4 py-2 text-sm text-gray-900">
                          {key.name}
                          {key.revokedAt && <span className="ml-2 rounded bg-red-100 px-2 py-0.5 text-xs text-red-700">revoked</span>}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-gray-600">{key.keyPrefix}</td>
                        <td className="px-4 py-2 text-xs text-gray-600">{key.scopes.join(', ')}</td>
                        <td className="px-4 py-2 text-sm text-gray-600">{key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleString() : 'Never'}</td>
                        <td className="px-4 py-2 text-right">
                          {!key.revokedAt && (
                            <button
                              type="button"
                              onClick={() => handleRevokeApiKey(key.apiKeyId)}
                              className="text-sm text-red-600 hover:text-red-900"
                            >
                              Revoke
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <DialogFooter>
              <button
                type="button"
                onClick={() => setApiKeysOpen(false)}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Invite External User Modal */}
        {showInviteModal && (
          <CreateInvitationModal
            roles={roles}
            onClose={() => setShowInviteModal(false)}
            onCreated={() => {
              loadUsers();
              setShowInviteModal(false);
            }}
          />
        )}

        {/* Invite Peter Lang User Modal */}
        {showInvitePLModal && (
          <InvitePLUserModal
            roles={roles}
            onClose={() => setShowInvitePLModal(false)}
            onCreated={() => {
              loadUsers();
              setShowInvitePLModal(false);
            }}
          />
        )}
        </div>
      </SecurityLayout>
    </ViewGuard>
  );
}
